<template>
	<li class="listing-item" :class="[stacked ? 'stacked' : '' ]">
		<a
			class="listing-item-header cursor-pointer"
			:class="{ 'w-1/2 flex-no-shrink -my-10 rounded-r': stacked }"
			:href="listingUrl"
		>
			<div class="listing-item-header-images" v-if="listing.photos.length">
				<div
					v-for="(photo, index) in listing.photos.slice(0, 3)"
					v-lazy:background-image="photo"
					:key="index"
				>
					<div
						v-if="listing.propertyCount > 3 && index == 2"
						class="listing-item-header-images-more"
					>+{{ listing.propertyCount - 2 }}</div>
					<div
						v-else-if="listing.entryType == 'combinedSpace' && listing.propertyCount > 1"
						class="listing-item-header-images-more listing-item-header-images-more--single"
					>{{ listing.propertyCount }}</div>
				</div>
			</div>
			<div class="listing-item-header-images" v-else>
				<div
					v-lazy:background-image="'/assets/img/properties/' + listing.propertyType.value + '.jpg'"
					style="filter: blur(8px);"
				></div>
				<div class="listing-item-header-images-more">
					<div class="icon" :class="listingColor.replace('text-', 'bg-') + '-40'">
						<ListingIcon color="white" :icon="listingIcon" />
					</div>
				</div>
			</div>
		</a>
		<div class="listing-item-details-wrapper">
			<a
				class="listing-item-details cursor-pointer"
				:class="{ '-ml-30': stacked }"
				:href="listingUrl"
			>
				<div class="listing-item-type" :class="{ 'listing-item-type-stacked': stacked }">
					<div
						class="absolute pin opacity-40 rounded-b"
						:class="[{ 'rounded-bl-none': stacked }, listingColor.replace('text-', 'bg-') ]"
					></div>
					<div class="relative">{{ propertyLabel }}</div>
				</div>

				<div class="listing-item-details-top">
					<div class="flex items-center" :class="listing.propertyType.value">
						<ListingIcon :color="listingColor" :icon="listingIcon" />
						<ListingIcon
							:color="types['package'].color"
							icon="package-only"
							class="ml-10"
							v-if="listing.settings.inPackageOnly"
						/>
						<div
							v-if="listing.settings.inPackageOnly"
							class="ml-15 text-gray700 font-bold font-secondary uppercase tracking-loose text-tiny bg-gray50 rounded py-5 px-8"
						>In {{ listing.package ? listing.package.type.label : (listing.listingType.sale ? 'Package' : 'Combined Space') }} Only</div>
						<div v-else class="ml-15 text-xl text-gray700 font-secondary font-bold" v-html="price"></div>
						<div
							v-if="listing.settings.buildToSuit"
							class="ml-20 text-gray700 font-bold font-secondary uppercase tracking-loose text-tiny bg-gray50 rounded py-5 px-8"
						>Build To Suit</div>
					</div>
					<div class="mt-20">
						<div class="font-bold text-gray700" v-if="listing.address.combined">{{ combined }}</div>
						<template v-else>
							<div class="font-bold text-gray700">{{ street }}</div>
							<div class="mt-10 text-sm">{{ cityStateZip }}</div>
						</template>
					</div>
				</div>
				<div
					class="listing-item-details-bottom"
					:class="{
					'package-two': listing.propertyCount == 2,
					'package-three': listing.propertyCount >= 3,
				}"
				>
					<div
						class="py-30 px-30 flex"
						v-if="(listing.sf && listing.sf.min > 0 && listing.sf.max > 0) || listing.acres > 0 || listing.package"
					>
						<div
							class="flex px-20 items-center"
							v-if="listing.sf && listing.sf.min > 0 && listing.sf.max > 0"
						>
							<span class="icon-m-measure text-gray300"></span>
							<div class="listing-info-item ml-10 text-gray500">{{ sf }}</div>
						</div>

						<div class="flex px-20 items-center" v-if="listing.acres > 0">
							<span class="icon-m-tree text-gray300"></span>
							<div class="listing-info-item ml-10 text-gray500">{{ acres }}</div>
						</div>

						<div class="flex px-20 ml-auto" v-if="listing.package">
							<a :href="listing.package.url">
								<span v-if="listing.settings.inPackageOnly" class="icon-m-package-only text-gray300"></span>
								<span v-else class="icon-m-package-included text-gray300"></span>
							</a>
						</div>
					</div>
				</div>
			</a>
		</div>
	</li>
</template>

<script>
	// import Vue components
	import ListingIcon from "./listing-icon.vue";

	// import helpers
	import stateToAbbreviation from "../../helpers/stateToAbbr.js";
	import formatPrice from "../../helpers/formatPrice.js";
	import addCommas from "../../helpers/addCommas.js";
	import lazyBackground from "../../directives/lazy-background";

	// Commercial property card used by property search and agent listing groups.
	export default {
		directives: {
			lazy: lazyBackground,
		},
		props: {
			listing: { type: Object, required: true },
			orientation: { type: String, default: 'horizontal' },
			requestedListingType: { type: String, default: '' },
			requestedLeaseRate: { type: String, default: '' },
		},
		data() {
			return {
				types: {
					land: {
						color: "text-green500",
						icon: "land"
					},
					office: {
						color: "text-blue500",
						icon: "commercial"
					},
					retail: {
						color: "text-purple500",
						icon: "retail"
					},
					residential: {
						color: "text-red500",
						icon: "townhouse"
					},
					industrial: {
						color: "text-orange500",
						icon: "industrial"
					},
					specialty: {
						color: "text-yellow500",
						icon: "other"
					},
					healthcare: {
						color: "text-pink500",
						icon: "health"
					},
					hospitality: {
						color: "text-magenta500",
						icon: "condo"
					},
					entertainment: {
						color: "text-teal500",
						icon: "entertainment"
					},
					package: {
						color: "text-gray500",
						icon: "package"
					},
					combinedSpace: {
						color: "text-gray500",
						icon: "package"
					}
				}
			};
		},
		computed: {
			listingUrl() {
				// Image stuff
				var socialVars = ''
				// if (this.listing.photos.length) {
				// 	const urlImage = encodeURIComponent(this.listing.photos[0]);
				// 	socialVars = `?image=${urlImage}`
				// }

				return this.listing.url + `${socialVars}`;
			},
			stacked() {
				return this.orientation == "vertical";
			},
			listingColor() {
				let propertyType = this.listing.propertyType.value;
				return this.types[propertyType].color;
			},
			listingIcon() {
				let propertyType = this.listing.propertyType.value;
				return this.types[propertyType].icon;
			},
			street() {
				const { street1, street2 } = this.listing.address;
				return street1 + (street2 ? ", " + street2 : "");
			},
			cityStateZip() {
				const { city, state, zip } = this.listing.address;
				return `${city}, ${stateToAbbreviation(state) || state} ${zip}`;
			},
			combined() {
				const states = this.listing.address.combined;
				let combined = "";
				Object.keys(states).forEach((state, i, array) => {
					const cities = states[state];
					const finalCity = cities.pop();
					combined += cities.length
						? cities.join(", ") + " & " + finalCity
						: finalCity;
					combined += ", " + (stateToAbbreviation(state) || state);
				});
				return combined;
			},
			price() {
				if (this.listing && this.listing.undisclosedPrice) return 'Undisclosed Price'
				if (this.listing && this.listing.negotiableRent) return 'Negotiable Rent'

				let pricing = this.listing.pricing || {};
				let salePrice = pricing && pricing.sale ? pricing.sale.price : null;
				let salePriceSqft =
					pricing && pricing.sale ? pricing.sale.sf : null;
				let leasePricing = pricing.lease || null;
				let leasePriceMo = leasePricing ? leasePricing.mo : null;
				let leasePriceSqft = leasePricing ? leasePricing.sf : null;
				let selectedUnit = leasePricing
					? leasePricing.selectedUnit
					: null;

				let price = "";

				let sfPrice = null;
				let moPrice = null;
				if (leasePriceSqft && leasePriceSqft.min !== null && leasePriceSqft.min !== undefined) {
					sfPrice = "$" + this.formatPrice(leasePriceSqft.min) + "/sf";
				}
				if (leasePriceMo !== null && leasePriceMo !== undefined) {
					moPrice = "$" + this.formatPrice(leasePriceMo) + "/mo";
				}

				if (salePrice && this.requestedListingType !== "lease") {
					price += "$" + this.formatPrice(salePrice);
					if (
						this.listing.settings.buildToSuit &&
						this.listing.sf &&
						this.listing.sf.min != this.listing.sf.max
					) {
						price += "+";
					}
				} else if (leasePricing && this.requestedListingType !== "sale") {
					if (this.requestedLeaseRate == "mo") {
						price += moPrice || sfPrice || "";
					} else if (this.requestedLeaseRate == "sf") {
						price += sfPrice || moPrice || "";
					} else if (selectedUnit == "mo") {
						price += moPrice || sfPrice || "";
					} else if (selectedUnit == "sqft") {
						price += sfPrice || moPrice || "";
					}
				}

				return price;
			},
			propertyLabel() {
				let propertyType = this.listing.propertyType.label;
				let listingType = this.listing.listingType;
				let status = this.listing.status.label;

				let listingTypeLabel = "";

				if (this.listing.settings.inPackageOnly && this.listing.package) {
					listingTypeLabel = "In " + this.listing.package.type.label;
				} else {
					if (status == "Closed") {
						listingTypeLabel = "";
						if (listingType.sale) {
							listingTypeLabel = "Sold";
						}
						if (listingType.sale && listingType.lease) {
							listingTypeLabel = "Closed";
						}
						if (listingType.lease) {
							listingTypeLabel = "Leased";
						}
					} else {
						listingTypeLabel = "For ";
						if (listingType.sale) {
							listingTypeLabel += "Sale";
						}
						if (listingType.sale && listingType.lease) {
							listingTypeLabel += " & ";
						}
						if (listingType.lease) {
							listingTypeLabel += "Lease";
						}
						if(status == "Pending"){
							listingTypeLabel += " • Pending";
						}
					}
				}

				// Determine base label
				let propertyLabel = propertyType;

				// Add Listing Type
				propertyLabel += " • " + listingTypeLabel;

				return propertyLabel;
			},
			sf() {
				let sf = this.listing.sf;
				let sfWithCommas = "";
				if (sf.min != sf.max) {
					sfWithCommas = addCommas(sf.min) + "-" + addCommas(sf.max);
				} else {
					sfWithCommas = addCommas(sf.min);
				}
				sfWithCommas +=
					this.listing.entryType != "property" ? " total" : "";
				sfWithCommas += " sf";

				if (sf) {
					return sfWithCommas;
				}
			},
			acres() {
				let acres = this.listing.acres;
				let acresWithCommas = addCommas(acres);
				acresWithCommas +=
					this.listing.entryType != "property" ? " total" : "";
				acresWithCommas += " acres";

				if (acres) {
					return acresWithCommas;
				}
			}
			// type() {
			// 	let type = this.listing.property.subType
			// 	type = this.convertCamel(type)
			// 	return type
			// },
		},
		methods: {
			convertCamel(string) {
				try {
					string = string.replace(/([A-Z])/g, " $1");
					string = string.replace(/^./, function(str) {
						return str.toUpperCase();
					});
				} catch (err) {
					console.error(err);
				}
				return string;
			},
			formatPrice(value) {
				if (value === null || value === undefined || value === '') {
					return '';
				}

				// Strip any non-numeric characters (except for decimal point)
				const formattedValue = value.toString().replace(/[^0-9.]/g, '');

				// Then convert to number
				const num = Number(formattedValue);

				if (isNaN(num)) {
					// Fallback: just return the original (string like "Negotiable Rent")
					return value;
				}

				let result = num.toFixed(2).replace('.00', '');
				return addCommas(result);
			}
		},
		components: {
			ListingIcon
		}
	};
</script>
